<script setup lang="ts">
/**
 * Мобильная шторка меню по бургеру: уровень 1 — сетка плиток разделов,
 * уровень 2 — список пунктов выбранного раздела.
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { UserMenu } from '@/features/session';
import { IconChevronLeft, IconChevronRight, IconLogo } from '@/shared/ui/icons';
import { BaseSidePanel } from '@/shared/ui/side-panel';

import type { MenuItem } from '../model/menu-types';

import { isMenuGroup } from '../model/menu-types';
import { findMenuGroup } from '../model/sidebar-panel-state';
import { useMenuBadges } from '../model/use-menu-badges';
import { useMenuNavigation } from '../model/use-menu-navigation';
import { useSidebar } from '../model/use-sidebar';
import { useUserMenuItems } from '../model/use-user-menu-items';

interface Props {
  /** Уже отфильтрованные по правам пункты меню */
  items: MenuItem[];
}

const props = defineProps<Props>();

const { isMobileMenuOpen, mobileGroupId, closeMobileMenu, openMobileGroup, closeMobileGroup } = useSidebar();
const { isLinkActive, isLinkHighlighted, handleLinkClick } = useMenuNavigation();
const { badgeCount, badgeLabel, formatBadgeCount } = useMenuBadges(() => props.items);
const userMenuItems = useUserMenuItems();

const openGroup = computed(() => findMenuGroup(props.items, mobileGroupId.value));
</script>

<template>
  <BaseSidePanel
    :open="isMobileMenuOpen"
    side="left"
    width="100%"
    label="Меню"
    @close="closeMobileMenu"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <button
          v-if="openGroup"
          type="button"
          class="-ml-2 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-(--base-text-color)"
          aria-label="Назад"
          @click="closeMobileGroup"
        >
          <IconChevronLeft
            :width="9"
            :height="16"
          />
        </button>
        <template v-else>
          <IconLogo
            class="text-(--sidebar-logo-color)"
            :width="32"
            :height="32"
          />
          <div class="ml-auto">
            <UserMenu :extra-items="userMenuItems" />
          </div>
        </template>
      </div>
    </template>

    <div class="overflow-x-hidden">
      <Transition
        mode="out-in"
        enter-active-class="transition-transform duration-200 ease-out"
        leave-active-class="transition-transform duration-200 ease-in"
        :enter-from-class="openGroup ? 'translate-x-full' : '-translate-x-full'"
        :leave-to-class="openGroup ? '-translate-x-full' : 'translate-x-full'"
      >
        <nav
          v-if="openGroup"
          :key="openGroup.id"
          :aria-label="openGroup.label"
        >
          <h2 class="px-5 pt-2 pb-4 text-2xl font-semibold text-(--base-text-color)">{{ openGroup.label }}</h2>

          <div class="mx-5 overflow-hidden rounded-2xl bg-(--base-main-bg)">
            <RouterLink
              v-for="(child, index) in openGroup.children"
              :key="child.id"
              v-slot="{ href, navigate }"
              :to="child.to"
              custom
            >
              <a
                :href="href"
                class="flex h-12 items-center justify-between px-4 text-base font-medium text-(--base-text-color)"
                :class="[
                  index > 0 ? 'border-t border-(--base-divider-soft)' : '',
                  isLinkActive(child) ? 'bg-(--messenger-active-item-bg)' : '',
                ]"
                :aria-current="isLinkActive(child) ? 'page' : undefined"
                @click="handleLinkClick(child, navigate, $event)"
              >
                {{ child.label }}
                <span
                  v-if="badgeCount(child) > 0"
                  class="mr-3 ml-auto rounded-full bg-(--base-accent-color) px-2 text-xs tabular-nums"
                  :aria-label="badgeLabel(child)"
                  >{{ formatBadgeCount(badgeCount(child)) }}</span
                >
                <IconChevronRight
                  class="shrink-0 text-(--base-secondary-text-color)"
                  :width="9"
                  :height="16"
                />
              </a>
            </RouterLink>
          </div>
        </nav>

        <nav
          v-else
          class="grid grid-cols-2 gap-4 p-5"
          aria-label="Разделы"
        >
          <template
            v-for="item in items"
            :key="item.id"
          >
            <RouterLink
              v-if="!isMenuGroup(item)"
              v-slot="{ href, navigate }"
              :to="item.to"
              custom
            >
              <a
                :href="href"
                class="relative flex h-22 flex-col items-start justify-between rounded-3xl p-4 text-base font-medium text-(--base-text-color)"
                :class="isLinkHighlighted(item) ? 'bg-(--messenger-active-item-bg)' : 'bg-(--base-main-bg)'"
                :aria-current="isLinkActive(item) ? 'page' : isLinkHighlighted(item) ? 'location' : undefined"
                @click="handleLinkClick(item, navigate, $event)"
              >
                <component
                  :is="item.icon"
                  v-if="item.icon"
                  class="text-(--base-secondary-text-color) [&_*]:[stroke-width:1.5px] [&_*]:[vector-effect:non-scaling-stroke]"
                  :width="24"
                  :height="24"
                />
                {{ item.label }}
                <span
                  v-if="badgeCount(item) > 0"
                  class="absolute top-3 right-3 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-(--base-accent-color) px-[5px] text-2xs leading-none font-semibold text-(--base-text-color) tabular-nums"
                  :title="badgeLabel(item)"
                  :aria-label="badgeLabel(item)"
                  >{{ formatBadgeCount(badgeCount(item)) }}</span
                >
              </a>
            </RouterLink>

            <button
              v-else
              type="button"
              class="relative flex h-22 flex-col items-start justify-between rounded-3xl bg-(--base-main-bg) p-4 text-left text-base font-medium text-(--base-text-color)"
              @click="openMobileGroup(item.id)"
            >
              <component
                :is="item.icon"
                v-if="item.icon"
                class="text-(--base-secondary-text-color) [&_*]:[stroke-width:1.5px] [&_*]:[vector-effect:non-scaling-stroke]"
                :width="24"
                :height="24"
              />
              {{ item.label }}
              <span
                v-if="badgeCount(item) > 0"
                class="absolute top-3 right-3 rounded-full bg-(--base-accent-color) px-2 text-xs tabular-nums"
                :aria-label="badgeLabel(item)"
                >{{ formatBadgeCount(badgeCount(item)) }}</span
              >
            </button>
          </template>
        </nav>
      </Transition>
    </div>
  </BaseSidePanel>
</template>
